import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FiSearch, FiPlus, FiEdit2, FiTrash2, FiPackage, FiAlertCircle, FiX, FiList, FiGrid, FiFile, FiFileText, FiFilter } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Products = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('all'); // 'all', 'low', 'normal'
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [formData, setFormData] = useState({
    name: '', sku: '', category_id: '', purchase_price: '', sale_price: '',
    stock: '0', min_stock: '5', warranty_months: '0', description: ''
  });

  const isAdmin = user?.role === 'admin';
  const isCaissier = user?.role === 'caissier';
  const canEdit = isAdmin || isCaissier;
  const canDelete = isAdmin;
  const canAdd = isAdmin || isCaissier;

  useEffect(() => { 
    fetchProducts(); 
    fetchCategories(); 
  }, [search, categoryFilter, stockFilter]);

  const fetchProducts = async () => {
    try {
      const response = await api.get(`/products?search=${search}&category=${categoryFilter}`);
      let productsData = response.data || [];
      
      // Appliquer le filtre de stock
      if (stockFilter === 'low') {
        productsData = productsData.filter(p => p.stock <= p.min_stock);
      } else if (stockFilter === 'normal') {
        productsData = productsData.filter(p => p.stock > p.min_stock);
      }
      
      setProducts(productsData);
      setFilteredProducts(productsData);
    } catch (error) { 
      toast.error('Erreur lors du chargement des produits'); 
    } finally { 
      setLoading(false); 
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/products/categories');
      setCategories(response.data || []);
    } catch (error) { 
      console.error(error); 
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.purchase_price || !formData.sale_price) {
      toast.error('Les champs requis sont manquants');
      return;
    }
    try {
      const submitData = {
        name: formData.name.trim(),
        sku: formData.sku?.trim() || null,
        category_id: formData.category_id ? parseInt(formData.category_id) : null,
        purchase_price: parseFloat(formData.purchase_price),
        sale_price: parseFloat(formData.sale_price),
        stock: parseInt(formData.stock) || 0,
        min_stock: parseInt(formData.min_stock) || 5,
        warranty_months: parseInt(formData.warranty_months) || 0,
        description: formData.description?.trim() || null
      };
      if (selectedProduct) {
        await api.put(`/products/${selectedProduct.id}`, submitData);
        toast.success('Produit modifié avec succès');
      } else {
        await api.post('/products', submitData);
        toast.success('Produit ajouté avec succès');
      }
      setShowModal(false);
      resetForm();
      fetchProducts();
    } catch (error) { 
      toast.error(error.response?.data?.message || 'Erreur'); 
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success('Produit supprimé avec succès');
      fetchProducts();
    } catch (error) { 
      toast.error('Erreur lors de la suppression'); 
    }
  };

  const resetForm = () => {
    setFormData({ 
      name: '', sku: '', category_id: '', purchase_price: '', sale_price: '', 
      stock: '0', min_stock: '5', warranty_months: '0', description: '' 
    });
    setSelectedProduct(null);
  };

  const openEditModal = (product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name || '',
      sku: product.sku || '',
      category_id: product.category_id ? String(product.category_id) : '',
      purchase_price: product.purchase_price ? String(product.purchase_price) : '',
      sale_price: product.sale_price ? String(product.sale_price) : '',
      stock: product.stock ? String(product.stock) : '0',
      min_stock: product.min_stock ? String(product.min_stock) : '5',
      warranty_months: product.warranty_months ? String(product.warranty_months) : '0',
      description: product.description || ''
    });
    setShowModal(true);
  };

  const exportToExcel = () => {
    const exportData = filteredProducts.map(p => ({ 
      'SKU': p.sku || 'N/A', 
      'Nom': p.name, 
      'Catégorie': getCategoryName(p.category_id), 
      "Prix d'achat": p.purchase_price, 
      'Prix de vente': p.sale_price, 
      'Stock': p.stock, 
      'Stock min': p.min_stock,
      'Statut stock': p.stock <= p.min_stock ? '⚠️ Stock faible' : '✅ Normal'
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Produits');
    XLSX.writeFile(wb, `produits_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Export Excel réussi');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFillColor(16, 185, 129);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setFontSize(18);
    doc.text('MULTI SERVICE TECHNOLOGIES', pageWidth / 2, 20, { align: 'center' });
    doc.setFontSize(10);
    doc.text('Liste des produits', pageWidth / 2, 32, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    const lowStockCount = filteredProducts.filter(p => p.stock <= p.min_stock).length;
    doc.text(`Total produits: ${filteredProducts.length} | ⚠️ Stock faible: ${lowStockCount}`, 14, 55);
    const tableHeaders = [['SKU', 'Nom', 'Prix vente', 'Stock', 'Statut']];
    const tableRows = filteredProducts.slice(0, 50).map(p => [
      p.sku || '-', 
      p.name, 
      `${p.sale_price} FCFA`, 
      p.stock.toString(),
      p.stock <= p.min_stock ? '⚠️ Stock faible' : '✅ Normal'
    ]);
    doc.autoTable({ 
      startY: 65, 
      head: tableHeaders, 
      body: tableRows, 
      theme: 'striped', 
      styles: { fontSize: 8 }, 
      headStyles: { fillColor: [16, 185, 129], textColor: [255, 255, 255] } 
    });
    doc.save(`produits_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Export PDF réussi');
  };

  const formatAmount = (amount) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(amount || 0);
  const getCategoryName = (id) => { const cat = categories.find(c => c.id === id); return cat ? cat.name : 'Non catégorisé'; };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Produits</h1>
        <div className="flex gap-3">
          <button onClick={exportToExcel} className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"><FiFile size={18} /> Excel</button>
          <button onClick={exportToPDF} className="bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"><FiFileText size={18} /> PDF</button>
          <div className="flex gap-2 border-l pl-3">
            <button onClick={() => setViewMode('list')} className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-green-100 text-green-600' : 'bg-gray-100'}`}><FiList size={18} /></button>
            <button onClick={() => setViewMode('grid')} className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-green-100 text-green-600' : 'bg-gray-100'}`}><FiGrid size={18} /></button>
          </div>
          {canAdd && (
            <button onClick={() => { resetForm(); setShowModal(true); }} className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2">
              <FiPlus size={18} /> Nouveau produit
            </button>
          )}
        </div>
      </div>

      {/* Filtres - Ajout du filtre Stock faible */}
      <div className="card mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Rechercher par nom ou SKU..." 
              className="input-field pl-10" 
              value={search} 
              onChange={(e) => setSearch(e.target.value)} 
            />
          </div>
          <select 
            className="input-field" 
            value={categoryFilter} 
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">Toutes les catégories</option>
            {categories.map(cat => <option key={cat.id} value={cat.name}>{cat.name}</option>)}
          </select>
          <div className="flex gap-2">
            <select 
              className="input-field flex-1" 
              value={stockFilter} 
              onChange={(e) => setStockFilter(e.target.value)}
            >
              <option value="all">📦 Tous les stocks</option>
              <option value="low">⚠️ Stock faible</option>
              <option value="normal">✅ Stock normal</option>
            </select>
            {stockFilter !== 'all' && (
              <button 
                onClick={() => setStockFilter('all')} 
                className="px-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors text-sm flex items-center"
              >
                <FiX size={16} />
              </button>
            )}
          </div>
        </div>
        
        {/* Indicateur du nombre de produits */}
        <div className="mt-3 flex justify-between items-center text-sm text-gray-500 border-t pt-3">
          <span>
            📊 <strong>{filteredProducts.length}</strong> produit(s) affiché(s)
            {stockFilter === 'low' && (
              <span className="ml-2 text-orange-600">⚠️ {filteredProducts.filter(p => p.stock <= p.min_stock).length} en stock faible</span>
            )}
          </span>
          <span className="text-xs">
            {filteredProducts.filter(p => p.stock <= p.min_stock).length > 0 && (
              <span className="text-orange-600">🔄 Stock faible détecté</span>
            )}
          </span>
        </div>
      </div>

      {/* Vue Liste */}
      {viewMode === 'list' && (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead className="border-b">
              <tr>
                <th className="pb-3">SKU</th>
                <th className="pb-3">Nom</th>
                <th className="pb-3">Catégorie</th>
                <th className="pb-3 text-right">Prix vente</th>
                <th className="pb-3 text-center">Stock</th>
                <th className="pb-3 text-center">Statut</th>
                <th className="pb-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => {
                const isLowStock = product.stock <= product.min_stock;
                return (
                  <tr key={product.id} className={`border-b hover:bg-gray-50 ${isLowStock ? 'bg-orange-50' : ''}`}>
                    <td className="py-3 text-sm">{product.sku || '-'}</td>
                    <td className="py-3 font-medium">{product.name}</td>
                    <td className="py-3">{getCategoryName(product.category_id)}</td>
                    <td className="py-3 text-right text-green-600">{formatAmount(product.sale_price)}</td>
                    <td className="py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${isLowStock ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                        {product.stock}
                      </span>
                    </td>
                    <td className="py-3 text-center">
                      {isLowStock ? (
                        <span className="flex items-center justify-center gap-1 text-orange-600 text-xs font-medium">
                          <FiAlertCircle size={14} /> Stock faible
                        </span>
                      ) : (
                        <span className="text-green-600 text-xs font-medium">✅ Normal</span>
                      )}
                    </td>
                    <td className="py-3 text-center">
                      <div className="flex justify-center gap-2">
                        {canEdit && <button onClick={() => openEditModal(product)} className="p-2 text-green-600 hover:bg-green-50 rounded-lg"><FiEdit2 size={16} /></button>}
                        {canDelete && <button onClick={() => handleDelete(product.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><FiTrash2 size={16} /></button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredProducts.length === 0 && (
            <div className="text-center py-12">
              <FiPackage className="mx-auto text-gray-300" size={48} />
              <p className="text-gray-500 mt-2">Aucun produit trouvé</p>
            </div>
          )}
        </div>
      )}

      {/* Vue Grille */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => {
            const isLowStock = product.stock <= product.min_stock;
            return (
              <div key={product.id} className={`card ${isLowStock ? 'border-orange-300 bg-orange-50' : ''}`}>
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isLowStock ? 'bg-orange-100' : 'bg-green-100'}`}>
                      <FiPackage className={isLowStock ? 'text-orange-600' : 'text-green-600'} />
                    </div>
                    <div>
                      <h3 className="font-semibold">{product.name}</h3>
                      <p className="text-xs text-gray-400">SKU: {product.sku || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {canEdit && <button onClick={() => openEditModal(product)} className="p-2 text-green-600 hover:bg-green-50 rounded-lg"><FiEdit2 size={16} /></button>}
                    {canDelete && <button onClick={() => handleDelete(product.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><FiTrash2 size={16} /></button>}
                  </div>
                </div>
                {isLowStock && (
                  <div className="mt-1 flex items-center gap-1 text-orange-600 text-xs font-medium">
                    <FiAlertCircle size={14} /> Stock faible ({product.stock} unités)
                  </div>
                )}
                <div className="border-t pt-3 mt-2">
                  <div className="flex justify-between">
                    <span className="text-xs text-gray-500">Prix vente</span>
                    <span className="text-green-600 font-bold">{formatAmount(product.sale_price)}</span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-gray-500">Stock</span>
                    <span className={`text-sm font-medium ${isLowStock ? 'text-red-600' : 'text-green-600'}`}>
                      {product.stock} unités {isLowStock && '⚠️'}
                    </span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-gray-500">Stock min</span>
                    <span className="text-sm text-gray-600">{product.min_stock}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {filteredProducts.length === 0 && viewMode === 'grid' && (
        <div className="text-center py-12">
          <FiPackage className="mx-auto text-gray-300" size={48} />
          <p className="text-gray-500 mt-2">Aucun produit trouvé</p>
        </div>
      )}

      {/* Modal Ajout/Modification */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                {selectedProduct ? '✏️ Modifier le produit' : '➕ Nouveau produit'}
              </h2>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="p-2 hover:bg-gray-100 rounded-lg">
                <FiX size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    📦 Nom du produit <span className="text-red-500">*</span>
                  </label>
                  <input type="text" className="input-field" placeholder="Ex: Smartphone Samsung Galaxy S24" value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">🔢 SKU (Code produit)</label>
                  <input type="text" className="input-field" placeholder="Ex: SKU-001-ABC" value={formData.sku || ''} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} />
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">📂 Catégorie</label>
                  <select className="input-field" value={formData.category_id || ''} onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}>
                    <option value="">Sélectionner une catégorie</option>
                    {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">💰 Prix d'achat (FCFA) <span className="text-red-500">*</span></label>
                  <input type="number" step="0.01" className="input-field" placeholder="Ex: 50000" value={formData.purchase_price || ''} onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })} required />
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">💵 Prix de vente (FCFA) <span className="text-red-500">*</span></label>
                  <input type="number" step="0.01" className="input-field" placeholder="Ex: 75000" value={formData.sale_price || ''} onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })} required />
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">📦 Stock initial</label>
                  <input type="number" className="input-field" placeholder="Ex: 10" value={formData.stock || '0'} onChange={(e) => setFormData({ ...formData, stock: e.target.value })} />
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">⚠️ Stock minimum d'alerte</label>
                  <input type="number" className="input-field" placeholder="Ex: 5" value={formData.min_stock || '5'} onChange={(e) => setFormData({ ...formData, min_stock: e.target.value })} />
                </div>
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">📅 Garantie (mois)</label>
                  <input type="number" className="input-field" placeholder="Ex: 6" value={formData.warranty_months || '0'} onChange={(e) => setFormData({ ...formData, warranty_months: e.target.value })} />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-1">📝 Description du produit</label>
                <textarea className="input-field" rows="4" placeholder="Description détaillée du produit..." value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
              </div>

              <div className="flex gap-3 pt-2 border-t">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="flex-1 bg-gray-200 text-gray-700 py-2.5 rounded-lg hover:bg-gray-300 transition-colors font-medium">
                  ❌ Annuler
                </button>
                <button type="submit" className="flex-1 bg-green-600 text-white py-2.5 rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-2">
                  {selectedProduct ? '💾 Modifier' : '✅ Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;